import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrendingUp, Users, Clock, Eye, MousePointer,
  Download, ChevronDown, ChevronRight, Sparkles,
  BarChart3, Zap, History
} from 'lucide-react';

export default function ImpactPanel() {
  const [expandedSections, setExpandedSections] = useState({
    engagement: true,
    cta: true,
    actions: true,
    value: true,
    insights: false,
    history: false
  });

  const [showValueDetails, setShowValueDetails] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Mock data - in production, fetch from analytics API
  const data = {
    engagementScore: 87,
    activeParticipants: 32,
    avgTimeInSession: '14 min',
    attentionLevel: 'High',
    ctas: [
      { name: 'Match NFT Offer', impressions: 32, clicks: 9, conversions: 2 },
      { name: 'Free Download – PDF', impressions: 32, clicks: 14, downloads: 13 },
    ],
    actions: [
      { user: 'Emma Wilson', action: 'clicked CTA', points: 3, time: '2s ago' },
      { user: 'Mike Chen', action: 'reacted', points: 1, time: '15s ago' },
      { user: 'Sarah Johnson', action: 'opened Poll', points: 2, time: '32s ago' },
      { user: 'David P.', action: 'stayed 15+ mins', points: 5, time: '1m ago' },
    ],
    valueBreakdown: {
      engagement: 62,
      interaction: 18,
      commerce: 5,
      total: 85
    },
    topParticipants: [
      { name: 'Emma Wilson', interactions: 9 },
      { name: 'Sarah Johnson', interactions: 7 },
      { name: 'Mike Chen', interactions: 5 },
    ],
    aiInsights: {
      working: [
        'High click-through on CTA #1',
        'Participants stayed longer than average',
        'Strong engagement between minutes 3-9'
      ],
      suggestion: 'Try activating a new CTA or launching a Poll to keep momentum high.'
    },
    history: {
      lastSession: 124,
      thisWeek: 267,
      bestSession: 412
    }
  };

  const SectionHeader = ({ title, section, icon: Icon }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between py-2 text-left"
    >
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#00c853]" />
        <span className="text-sm font-semibold text-white">{title}</span>
      </div>
      {expandedSections[section] ? (
        <ChevronDown className="w-4 h-4 text-gray-400" />
      ) : (
        <ChevronRight className="w-4 h-4 text-gray-400" />
      )}
    </button>
  );

  // Custom Progress Bar component
  const ProgressBar = ({ value, className = '' }) => (
    <div className={`h-2 bg-white/10 rounded-full overflow-hidden ${className}`}>
      <div
        className="h-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-full transition-all duration-500"
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-4 space-y-4">

        {/* Engagement Summary */}
        <div className="bg-white/5 rounded-xl p-4">
          <SectionHeader title="Engagement Summary" section="engagement" icon={BarChart3} />
          <AnimatePresence>
            {expandedSections.engagement && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">Live Engagement Score</span>
                    <span className="text-2xl font-bold text-[#00c853]">{data.engagementScore}</span>
                  </div>
                  <ProgressBar value={data.engagementScore} />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        <Users className="w-3.5 h-3.5" />
                        <span className="text-xs">Active</span>
                      </div>
                      <span className="text-lg font-semibold text-white">{data.activeParticipants}</span>
                    </div>
                    <div className="bg-white/5 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-gray-400 mb-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span className="text-xs">Avg Time</span>
                      </div>
                      <span className="text-lg font-semibold text-white">{data.avgTimeInSession}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-white/10">
                    <span className="text-sm text-gray-400">Attention Level</span>
                    <span className="text-sm font-medium text-[#00c853]">{data.attentionLevel}</span>
                  </div>
                  <p className="text-[10px] text-gray-500">
                    Powered by the Rally Network - timestamped and verified.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* CTA Performance */}
        <div className="bg-white/5 rounded-xl p-4">
          <SectionHeader title="CTA Performance" section="cta" icon={MousePointer} />
          <AnimatePresence>
            {expandedSections.cta && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  {data.ctas.map((cta, index) => (
                    <div key={index} className="bg-white/5 rounded-lg p-3">
                      <p className="text-sm font-medium text-white mb-2">"{cta.name}"</p>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                            <Eye className="w-3 h-3" />
                          </div>
                          <span className="text-sm font-semibold text-white">{cta.impressions}</span>
                          <p className="text-[10px] text-gray-500">Views</p>
                        </div>
                        <div>
                          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                            <MousePointer className="w-3 h-3" />
                          </div>
                          <span className="text-sm font-semibold text-white">{cta.clicks}</span>
                          <p className="text-[10px] text-gray-500">Clicks</p>
                        </div>
                        <div>
                          <div className="flex items-center justify-center gap-1 text-gray-400 mb-1">
                            {cta.conversions !== undefined ? <Zap className="w-3 h-3" /> : <Download className="w-3 h-3" />}
                          </div>
                          <span className="text-sm font-semibold text-[#00c853]">
                            {cta.conversions !== undefined ? cta.conversions : cta.downloads}
                          </span>
                          <p className="text-[10px] text-gray-500">
                            {cta.conversions !== undefined ? 'Conv.' : 'Downloads'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Participant Actions Feed */}
        <div className="bg-white/5 rounded-xl p-4">
          <SectionHeader title="Participant Actions" section="actions" icon={Zap} />
          <AnimatePresence>
            {expandedSections.actions && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-2">
                  {data.actions.map((action, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white truncate">
                          <span className="font-medium">{action.user}</span>
                          <span className="text-gray-400"> {action.action}</span>
                        </p>
                        <p className="text-[10px] text-gray-500">{action.time}</p>
                      </div>
                      <span className="text-xs font-medium text-[#00c853] ml-2">+{action.points} pts</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Value Generated */}
        <div className="bg-white/5 rounded-xl p-4">
          <SectionHeader title="Your Value Today" section="value" icon={Sparkles} />
          <AnimatePresence>
            {expandedSections.value && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-3">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Engagement Value</span>
                      <span className="text-white">{data.valueBreakdown.engagement} pts</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Interaction Value</span>
                      <span className="text-white">{data.valueBreakdown.interaction} pts</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-400">Commerce Value</span>
                      <span className="text-white">{data.valueBreakdown.commerce} pts</span>
                    </div>
                    <div className="h-px bg-white/10 my-2" />
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">Total Value</span>
                      <span className="text-xl font-bold text-[#00c853]">{data.valueBreakdown.total} pts</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500">
                    Your Value Score determines how many rewards you earn at the end of the session.
                  </p>

                  <button
                    onClick={() => setShowValueDetails(!showValueDetails)}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    <span>Details</span>
                    <ChevronRight className={`w-3 h-3 transition-transform ${showValueDetails ? 'rotate-90' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {showValueDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="bg-white/5 rounded-lg p-3 space-y-2 text-xs text-gray-400">
                          <p>Verified on the Rally Network</p>
                          <p>Earns tokens when session ends</p>
                          <p>Multiplied by Boost NFT (if active)</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Audience Insights */}
        <div className="bg-white/5 rounded-xl p-4">
          <SectionHeader title="Audience Insights" section="insights" icon={Sparkles} />
          <AnimatePresence>
            {expandedSections.insights && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-4">
                  <div>
                    <p className="text-xs font-medium text-gray-400 mb-2">Top Engaged Participants</p>
                    <div className="space-y-1.5">
                      {data.topParticipants.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span className="text-white">{i + 1}. {p.name}</span>
                          <span className="text-gray-400">{p.interactions} interactions</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-[#00c853]/10 rounded-lg p-3 border border-[#00c853]/20">
                    <p className="text-xs font-medium text-[#00c853] mb-2">What's Working</p>
                    <ul className="space-y-1">
                      {data.aiInsights.working.map((insight, i) => (
                        <li key={i} className="text-xs text-gray-300">• {insight}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
                    <p className="text-xs font-medium text-purple-400 mb-1">Suggested Next Step</p>
                    <p className="text-xs text-gray-300">{data.aiInsights.suggestion}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Past Sessions */}
        <div className="bg-white/5 rounded-xl p-4">
          <SectionHeader title="Past Sessions" section="history" icon={History} />
          <AnimatePresence>
            {expandedSections.history && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Last Session</span>
                    <span className="text-white font-medium">{data.history.lastSession} Value</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">This Week</span>
                    <span className="text-white font-medium">{data.history.thisWeek} Value</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Your Best Session</span>
                    <span className="text-[#00c853] font-medium">{data.history.bestSession} Value</span>
                  </div>
                  <button className="w-full text-center text-xs text-[#00c853] hover:underline pt-2">
                    View Full Analytics
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </div>
    </div>
  );
}
